import clsx from 'clsx';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import { upgrade_subscription_path } from '@routes';

export const AUDIO_LISTEN_ONBOARDING_COOKIE = 'audio-listen-onboarding-dismissed';

type ListenOnboardingPanelProps = {
  isMonthlySubscriber: boolean;
  onDismiss: () => void;
};

const ListenOnboardingPanel = ({ isMonthlySubscriber, onDismiss }: ListenOnboardingPanelProps) => {
  const toolTipText = isMonthlySubscriber
    ? 'Audio articles are available to Annual and Pro subscribers.'
    : 'Play the audio version of this article to listen on the go.';
  const toolTipButtonText = isMonthlySubscriber ? 'Upgrade today' : 'Got it';

  const handleToolTipButtonClick = () => {
    if (isMonthlySubscriber) {
      window.location.href = upgrade_subscription_path();
    } else {
      onDismiss();
    }
  };
  return (
    <div
      role="dialog"
      aria-labelledby="audio-listen-onboarding-title"
      aria-modal="false"
      className={clsx(
        'shadow-lg pointer-events-auto absolute bottom-full left-1/2 z-20 mb-12 w-[min(100vw-48px,400px)] -translate-x-1/2 rounded-4 bg-grayscale-charcoal px-20 pb-16 pt-20 text-white print:hidden',
        "after:absolute after:-bottom-8 after:left-1/2 after:h-16 after:w-16 after:-translate-x-1/2 after:rotate-45 after:rounded-br-4 after:bg-grayscale-charcoal after:content-['']"
      )}
    >
      <button
        type="button"
        className="absolute right-8 top-8 flex h-32 w-32 touch-manipulation items-center justify-center border-none bg-transparent text-white"
        onClick={onDismiss}
        aria-label="Close audio tip"
      >
        <IconClose className="stroke-2" height={12} width={12} />
      </button>
      <p
        id="audio-listen-onboarding-title"
        className="m-0 pr-28 font-suisse-intl text-body-md font-normal leading-snug text-white"
      >
        {toolTipText}
      </p>
      <div className="mt-8">
        <TextButton
          as={isMonthlySubscriber ? 'link' : 'button'}
          type="button"
          className="inline !w-fit p-0 font-suisse-intl text-button-md text-primary-pink underline"
          onClick={handleToolTipButtonClick}
        >
          {toolTipButtonText}
        </TextButton>
      </div>
    </div>
  );
};

export type AudioListenOnboardingTriggerProps = {
  children: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
  isMonthlySubscriber: boolean;
  className?: string;
  /** Fires when the onboarding popover opens or closes (e.g. to sync Listen icon animation). */
  onListenOnboardingOpenChange?: (open: boolean) => void;
};

/**
 * Non-monthly: first-time listen tip on page load (cookie after dismiss).
 * Monthly: paywall tip opens on Listen click; closes via X or outside click only.
 */
const AudioListenOnboardingTrigger = ({
  children,
  isMonthlySubscriber,
  className,
  onListenOnboardingOpenChange,
}: AudioListenOnboardingTriggerProps) => {
  const containerRef = useRef<HTMLSpanElement>(null);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isMonthlySubscriber) return;
    if (!Cookies.get(AUDIO_LISTEN_ONBOARDING_COOKIE)) {
      setVisible(true);
    }
  }, [isMonthlySubscriber]);

  useEffect(() => {
    onListenOnboardingOpenChange?.(visible);
  }, [visible, onListenOnboardingOpenChange]);

  const dismiss = useCallback(() => {
    if (!isMonthlySubscriber) {
      const expires = new Date();
      expires.setMonth(expires.getMonth() + 6);
      Cookies.set(AUDIO_LISTEN_ONBOARDING_COOKIE, 'true', { expires });
    }
    setVisible(false);
  }, [isMonthlySubscriber]);

  useEffect(() => {
    if (!visible) return;
    const onPointerDown = (event: PointerEvent) => {
      const node = containerRef.current;
      if (node && !node.contains(event.target as Node)) {
        dismiss();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [visible, dismiss]);

  const handleListenClick: React.MouseEventHandler = (event) => {
    if (isMonthlySubscriber) {
      setVisible(true);
    }
    children.props.onClick?.(event);
  };

  return (
    <span ref={containerRef} className={className}>
      {visible && (
        <ListenOnboardingPanel isMonthlySubscriber={isMonthlySubscriber} onDismiss={dismiss} />
      )}
      {React.cloneElement(children, { onClick: handleListenClick })}
    </span>
  );
};

export default AudioListenOnboardingTrigger;
